import {Image, Link} from '@shopify/hydrogen';
import {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import clsx from 'clsx';

import type {SanityMenuLink} from '../../types';
import {useScrollFreeze} from '../../hooks/useScrollFreeze';

/**
 * A server component that defines the navigation for a web storefront
 */

type Props = {
  menuLinks: SanityMenuLink[];
  flip: boolean;
  sideMenuOpen: boolean;
  setSideMenuOpen: any;
  setActiveSideMenuItem: any;
  activeSideMenuItem: number;
};

const excerptVars = {
  initital: {
    opacity: 0,
    height: 0,
  },
  animate: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

const LinkSwitch = ({external, externalLink, link, children, ...rest}: any) => {
  switch (external) {
    case true:
      return (
        <a
          aria-label="a link taking you to the page displayed by the label"
          href={externalLink?.url}
          target={externalLink?.newWindow ? '_blank' : '_self'}
          rel="noreferrer"
          {...rest}
        >
          {children}
        </a>
      );

    default:
      return (
        <Link
          aria-label="a link taking you to the page displayed by the label"
          to={link?.slug || '/'}
          {...rest}
        >
          {children}
        </Link>
      );
  }
};

const MenuItem = ({
  excerpt,
  image,
  link,
  active,
  external,
  externalLink,
  ...rest
}: any) => {
  return (
    <LinkSwitch
      external={external}
      externalLink={externalLink}
      link={link}
      className={clsx([
        'transition-standard flex items-center gap-x-4 overflow-hidden border-b border-light-grey py-5',
        active ? 'h-[200px]' : 'h-[85px]',
      ])}
      {...rest}
    >
      <div
        className={clsx([
          'transition-standard relative h-auto w-auto',
          active ? 'w-[200px]' : 'w-[120px]',
        ])}
      >
        <Image
          width="inherit"
          height="inherit"
          data={image?.asset}
          alt={link?.title || externalLink?.title}
          className="transition-standard block w-auto"
        />
      </div>
      <div className="transition-standard h-full w-full">
        <span
          className={clsx([
            'transition-standard block font-bold',
            active ? 'blockH6 mt-4 text-orange ' : 'blockH7 mt-2',
          ])}
        >
          {link?.title || externalLink?.title}
        </span>
        <AnimatePresence mode="wait">
          {active && (
            <motion.p
              variants={excerptVars}
              initial="initital"
              animate="animate"
              exit="exit"
              key={active}
              className="richH7 mt-3"
            >
              {excerpt}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </LinkSwitch>
  );
};

export const SideBar = ({
  menuLinks,
  activeSideMenuItem,
  sideMenuOpen,
  onClick,
}: any) => {
  useScrollFreeze();
  const [activeLink, setActiveLink] = useState<any>(false);

  const sideVars = {
    initial: {
      x: sideMenuOpen ? '-100%' : 0,
      opacity: 0,
    },
    animate: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
    exit: {
      opacity: 0,
      x: sideMenuOpen ? '-100%' : 0,
      transition: {
        duration: 0.3,
        delay: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <>
      <motion.button
        initial={{opacity: 0}}
        animate={{opacity: 1, transition: {delay: 0.3}}}
        exit={{opacity: 0}}
        onClick={onClick}
        className="fixed left-[450px] top-16 z-[999] flex h-14 w-14 items-center justify-center rounded-full bg-orange/30 2xl:left-[500px]"
      >
        <svg
          width="18"
          height="19"
          viewBox="0 0 18 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.5137 1.37924C17.0051 0.870681 16.1807 0.87068 15.6722 1.37924L8.99609 8.05533L2.32 1.37924C1.81144 0.87068 0.987046 0.870681 0.478488 1.37924C-0.0300711 1.8878 -0.0300703 2.7122 0.478488 3.22075L7.15458 9.89684L0.478488 16.5729C-0.0300718 17.0815 -0.0300698 17.9059 0.478489 18.4145C0.987048 18.923 1.81144 18.923 2.32 18.4145L8.9961 11.7384L15.6722 18.4144C16.1807 18.923 17.0051 18.923 17.5137 18.4145C18.0223 17.9059 18.0223 17.0815 17.5137 16.5729L10.8376 9.89685L17.5137 3.22076C18.0223 2.7122 18.0223 1.8878 17.5137 1.37924Z"
            fill="#F27021"
          />
        </svg>
      </motion.button>
      <motion.div
        onClick={onClick}
        initial={{opacity: 0}}
        animate={{opacity: 1, transition: {delay: 0.3}}}
        exit={{opacity: 0}}
        className="fixed bottom-0 left-0 top-0 z-[60] h-screen w-screen bg-black/30"
      />
      <motion.div
        variants={sideVars}
        initial="initial"
        animate="animate"
        exit="exit"
        className="fixed bottom-0 left-0 top-0 z-[60] h-screen w-[40vw] max-w-[600px] bg-white"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSideMenuItem}
            initial={{opacity: 0}}
            animate={{opacity: 1, transition: {duration: 0.3, delay: 0.1}}}
            exit={{opacity: 0}}
            className="px-10 py-space-m"
          >
            <div>
              {menuLinks[activeSideMenuItem].items?.map(
                (item: any, i: number) => (
                  <MenuItem
                    key={i}
                    {...item}
                    onClick={onClick}
                    onMouseEnter={() => setActiveLink(i)}
                    onMouseLeave={() => setActiveLink(false)}
                    active={activeLink === i}
                  />
                ),
              )}
            </div>

            {menuLinks[activeSideMenuItem].bottomLink && (
              <div className="mt-10">
                <Link
                  aria-label="a link taking you to the page displayed by the label"
                  onClick={onClick}
                  className="linkTextNavigation blockH7 font-bold text-orange"
                  to={menuLinks[activeSideMenuItem].bottomLink?.slug || '/'}
                >
                  {menuLinks[activeSideMenuItem].bottomLink?.title}
                </Link>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default function Navigation({
  menuLinks,
  flip,
  sideMenuOpen,
  setSideMenuOpen,
  activeSideMenuItem,
  setActiveSideMenuItem,
}: Props) {
  const RenderLinks = ({link, i}: any) => {
    switch (link._type) {
      case 'linkExternal':
        return (
          <div className="flex items-center" key={link._key}>
            <a
              onClick={() => setSideMenuOpen(false)}
              className="transition-standard border-b-2 border-orange/0 hover:border-orange"
              href={link.url}
              rel="noreferrer"
              target={link.newWindow ? '_blank' : '_self'}
              aria-label="a link taking you to the page displayed by the label"
            >
              {link.title}
            </a>
          </div>
        );
      case 'linkInternal':
        if (!link.slug) {
          return null;
        }

        return (
          <div className="flex items-center" key={link._key}>
            <Link
              aria-label="a link taking you to the page displayed by the label"
              onClick={() => setSideMenuOpen(false)}
              className="transition-standard border-b-2 border-orange/0 hover:border-orange"
              to={link.slug}
            >
              {link.title}
            </Link>
          </div>
        );
      case 'sideMenu':
        return (
          <div className="flex items-center">
            <button
              onClick={() => {
                setSideMenuOpen(true);
                setActiveSideMenuItem(i);
              }}
              className={`transition-standard border-b-2 border-orange/0 hover:border-orange ${
                sideMenuOpen &&
                activeSideMenuItem === i &&
                'border-orange text-orange'
              }`}
            >
              {link.title}
            </button>
          </div>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <nav
        className={clsx(
          'transition-standard z-20 hidden items-stretch justify-start gap-6 font-bold text-base',
          'w-min whitespace-nowrap font-bold xl:flex',
          flip || sideMenuOpen ? 'text-dark-gray' : 'text-white',
        )}
      >
        {menuLinks?.map((link, i) => (
          <RenderLinks key={i} link={link} i={i} />
        ))}
      </nav>
    </>
  );
}
